<template>
  <nav class="navbar" :class="{ 'navbar-scrolled': isScrolled }">
    <div class="navbar-container">
      <div class="navbar-brand">
        <router-link to="/" class="brand-link">
          <img src="../assets/logo.png" alt="Partycord logo" class="navbar-logo">
          <span class="brand-text">Partycord</span>
        </router-link>
      </div>

      <!-- Mobile menu button -->
      <button 
        class="mobile-menu-toggle"
        @click="toggleMenu"
        :aria-expanded="showMenu"
        aria-label="Toggle navigation menu"
      >
        <span class="hamburger-line" :class="{ 'active': showMenu }"></span>
        <span class="hamburger-line" :class="{ 'active': showMenu }"></span>
        <span class="hamburger-line" :class="{ 'active': showMenu }"></span>
      </button>

      <!-- Desktop navigation -->
      <div class="navbar-nav desktop-nav">
        <router-link to="/" class="nav-item" active-class="active">Home</router-link>
        <router-link to="/commands" class="nav-item" active-class="active">Commands</router-link>
        <router-link to="/guides" class="nav-item" active-class="active">Guides</router-link>
        <router-link to="/changelog" class="nav-item" active-class="active">Changelog</router-link>
        <button @click="joinServer" class="nav-item nav-button">Support Server</button>
        <button @click="becomePatron" class="nav-item nav-button patreon-btn">Become a Patron</button>
      </div>

      <!-- Invite button (always visible on desktop) -->
      <div class="navbar-actions desktop-nav">
        <button @click="inviteBot" class="invite-button">
          <fa icon="plus" />
          Invite Bot
        </button>
      </div>
    </div>

    <!-- Mobile menu overlay -->
    <div 
      class="menu-overlay" 
      :class="{ 'active': showMenu }" 
      @click="closeMenu"
      @keydown.escape="closeMenu"
    ></div>

    <!-- Mobile navigation menu -->
    <nav 
      class="mobile-menu" 
      :class="{ 'active': showMenu }"
      :inert="!showMenu"
    >
      <div class="mobile-menu-content">
        <div class="mobile-menu-header">
          <span class="mobile-brand">Partycord</span>
          <button 
            class="close-button"
            @click="closeMenu"
            aria-label="Close navigation menu"
          >
            <fa icon="times" />
          </button>
        </div>

        <div class="mobile-nav-items">
          <router-link to="/" class="mobile-nav-item" @click="closeMenu">
            <fa icon="home" />
            Home
          </router-link>
          <router-link to="/commands" class="mobile-nav-item" @click="closeMenu">
            <fa icon="list" />
            Commands
          </router-link>
          <router-link to="/guides" class="mobile-nav-item" @click="closeMenu">
            <fa icon="list" />
            Guides
          </router-link>
          <router-link to="/changelog" class="mobile-nav-item" @click="closeMenu">
            <fa icon="list" />
            Changelog
          </router-link>
          <button @click="joinServer" class="mobile-nav-item mobile-nav-button">
            <fa :icon="['fab', 'discord']" />
            Support Server
          </button>
          <button @click="becomePatron" class="mobile-nav-item mobile-nav-button patreon-mobile">
            <fa icon="heart" />
            Become a Patron
          </button>
          <button @click="inviteBot" class="mobile-invite-button">
            <fa icon="plus" />
            Invite Bot
          </button>
        </div>
      </div>
    </nav>
  </nav>
</template>

<script>
export default {
  name: 'NavbarMenu',
  data() {
    return {
      showMenu: false,
      isScrolled: false
    };
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('keydown', this.handleKeyDown);
    // Prevent body scroll when menu is open
    this.$watch('showMenu', (newVal) => {
      if (newVal) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    });
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('keydown', this.handleKeyDown);
    document.body.style.overflow = '';
  },
  emits: ['invite-bot', 'join-server', 'become-patron'],
  methods: {
    toggleMenu() {
      this.showMenu = !this.showMenu;
    },
    closeMenu() {
      this.showMenu = false;
    },
    inviteBot() {
      this.$emit('invite-bot');
      this.closeMenu();
    },
    joinServer() {
      this.$emit('join-server');
      this.closeMenu();
    },
    becomePatron() {
      this.$emit('become-patron');
      this.closeMenu();
    },
    handleScroll() {
      this.isScrolled = window.scrollY > 50;
    },
    handleKeyDown(event) {
      if (event.key === 'Escape' && this.showMenu) {
        this.closeMenu();
      }
    },
    handleOutsideClick(event) {
      const mobileMenu = this.$el?.querySelector('.mobile-menu');
      const toggleButton = this.$el?.querySelector('.mobile-menu-toggle');
      
      if (this.showMenu && mobileMenu && toggleButton && 
          !mobileMenu.contains(event.target) && 
          !toggleButton.contains(event.target)) {
        this.closeMenu();
      }
    }
  }
};
</script>

<style scoped>
/* CSS Custom Properties (local overrides) */
.navbar {
  --navbar-height: 70px;
  --navbar-bg: rgba(22, 23, 28, 0.85);
  --navbar-bg-scrolled: rgba(15, 16, 21, 0.92);
}

.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: var(--navbar-height);
  background: var(--navbar-bg);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 9999;
  transition: all var(--transition-normal);
}

.navbar-scrolled {
  background: var(--navbar-bg-scrolled);
  box-shadow: var(--shadow-lg);
}

.navbar-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  max-width: var(--container-max-width);
  margin: 0 auto;
  padding: 0 var(--container-padding);
}

/* Brand */
.navbar-brand {
  flex-shrink: 0;
}

.navbar-logo {
  width: 2rem;
  height: 2rem;
  margin-right: var(--spacing-sm);
}

.brand-link {
  text-decoration: none;
  color: inherit;
}

.brand-text {
  font-size: 1.5rem;
  font-weight: bold;
  background: var(--color-gradient-primary);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  transition: all var(--transition-normal);
}

/* Desktop Navigation */
.navbar-nav {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.nav-item {
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius-lg);
  color: var(--color-text-secondary);
  text-decoration: none;
  font-weight: 500;
  font-size: 0.95rem;
  background: none;
  border: none;
  cursor: pointer;
  transition: all var(--transition-normal);
  position: relative;
  white-space: nowrap;
}

.nav-item:hover {
  color: var(--color-text-primary);
  background: rgba(255, 255, 255, 0.08);
}

.nav-item.active {
  color: var(--color-text-primary);
  background: rgba(255, 255, 255, 0.1);
}

.nav-item.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  height: 2px;
  background: var(--color-gradient-primary);
  border-radius: 1px;
}

.patreon-btn {
  background: linear-gradient(135deg, var(--color-accent-patreon), #ff8080) !important;
  color: var(--color-text-primary) !important;
}

.patreon-btn:hover {
  background: linear-gradient(135deg, var(--color-accent-patreon-hover), #ff9999) !important;
  transform: translateY(-1px);
}

/* Invite Button */
.invite-button {
  background: var(--color-accent-discord);
  color: var(--color-text-primary);
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: var(--border-radius-lg);
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-normal);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
}

.invite-button:hover {
  background: var(--color-accent-discord-hover);
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

.invite-button:focus-visible {
  outline: 2px solid var(--color-text-primary);
  outline-offset: 2px;
}

/* Mobile Menu Toggle */
.mobile-menu-toggle {
  display: none;
  flex-direction: column;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 9999;
}

.hamburger-line {
  width: 100%;
  height: 2px;
  background: var(--color-text-primary);
  margin: 3px 0;
  transition: all 0.3s ease;
  transform-origin: center;
}

.hamburger-line.active:nth-child(1) {
  transform: rotate(45deg) translate(6px, 6px);
}

.hamburger-line.active:nth-child(2) {
  opacity: 0;
}

.hamburger-line.active:nth-child(3) {
  transform: rotate(-45deg) translate(6px, -6px);
}

/* Mobile Menu Overlay */
.menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 9998;
  opacity: 0;
  visibility: hidden;
  transition: all var(--transition-normal);
}

.menu-overlay.active {
  opacity: 1;
  visibility: visible;
}

/* Mobile Menu */
.mobile-menu {
  position: fixed;
  top: 0;
  right: 0;
  width: min(350px, 85vw);
  height: 100vh;
  background: var(--color-bg-secondary);
  transform: translateX(100%);
  transition: transform var(--transition-normal);
  z-index: 9999;
  box-shadow: var(--shadow-xl);
  overflow-y: auto;
}

.mobile-menu.active {
  transform: translateX(0);
}

.mobile-menu-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: var(--spacing-lg);
}

.mobile-menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: var(--spacing-lg);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: var(--spacing-lg);
}

.mobile-brand {
  font-size: 1.25rem;
  font-weight: bold;
  background: var(--color-gradient-primary);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.close-button {
  background: none;
  border: none;
  color: var(--color-text-primary);
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: var(--border-radius-sm);
  transition: all var(--transition-normal);
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--color-accent-discord);
}

/* Mobile Navigation Items */
.mobile-nav-items {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
}

.mobile-nav-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: var(--border-radius-md);
  color: var(--color-text-secondary);
  text-decoration: none;
  font-weight: 500;
  font-size: 1rem;
  background: none;
  border: none;
  cursor: pointer;
  transition: all var(--transition-normal);
  text-align: left;
}

.mobile-nav-item:hover {
  background: rgba(255, 255, 255, 0.08);
  color: var(--color-text-primary);
}

.mobile-nav-item.router-link-active {
  background: rgba(255, 255, 255, 0.1);
  color: var(--color-text-primary);
}

.patreon-mobile {
  background: linear-gradient(135deg, var(--color-accent-patreon), #ff8080) !important;
  color: var(--color-text-primary) !important;
}

.patreon-mobile:hover {
  background: linear-gradient(135deg, var(--color-accent-patreon-hover), #ff9999) !important;
}

.mobile-invite-button {
  background: var(--color-accent-discord);
  color: var(--color-text-primary);
  border: none;
  padding: 1rem;
  border-radius: var(--border-radius-md);
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-normal);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  font-size: 1rem;
  margin-top: var(--spacing-lg);
}

.mobile-invite-button:hover {
  background: var(--color-accent-discord-hover);
  transform: translateY(-1px);
}

/* Responsive Design */
@media (max-width: 1024px) {
  .navbar-nav {
    gap: 0.25rem;
  }
  
  .nav-item {
    padding: 0.4rem 0.8rem;
    font-size: 0.9rem;
  }
}

@media (max-width: 768px) {
  :root {
    --navbar-padding: 1rem;
  }
  
  .desktop-nav {
    display: none;
  }
  
  .mobile-menu-toggle {
    display: flex;
  }
  
  .navbar-container {
    padding: 0 var(--spacing-md);
  }
}

@media (max-width: 480px) {
  .navbar-container {
    padding: 0 var(--spacing-sm);
  }
  
  .brand-text {
    font-size: 1.25rem;
  }
  
  .mobile-menu {
    width: 100vw;
  }
  
  .mobile-menu-content {
    padding: var(--spacing-md);
  }
}

/* Focus states for accessibility */
.nav-item:focus-visible,
.mobile-nav-item:focus-visible,
.mobile-menu-toggle:focus-visible {
  outline: 2px solid var(--color-accent-discord);
  outline-offset: 2px;
}

/* Ensure mobile menu is not tabbable when closed */
.mobile-menu:not(.active) {
  visibility: hidden;
}

.mobile-menu.active {
  visibility: visible;
}
</style>
